import { Button, DatePicker, Select } from 'antd'
import moment from 'moment'
import React, { useEffect, useState, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { H3 } from 'src/branding/typography'
import { resetOrdersFilter, setOrdersFilter } from 'src/features/FilterSlice'
import { setOrderedProductsPage } from 'src/features/OrderedProductsSlice'
import { fetchOrders } from 'src/features/OrdersSlice'
import { fetchAllSuppliers } from 'src/features/SupplierSlice'
import { findUnique } from 'src/utils/arrayFunctions'
import styled from 'styled-components/macro'

const { Option } = Select

const OrdersFilter = ({ venueId = '' }) => {
  const dispatch = useDispatch()
  const filter = useSelector(state => state.filter.orders)
  const savedFilter = useSelector(
    state => state.filter.ordersByVenue?.[venueId],
  )
  const allSuppliers = useSelector(state => state.suppliers.suppliers)
  const venuesOptions = useSelector(state => state.auth.account.venues)
  const simplifiedOrders = useSelector(state => state.orders.simpliifiedOrders)
  const [isEndOpen, setIsEndOpen] = useState(false)
  const [typesOptions, setTypesOptions] = useState([])
  const [suppliersOptions, setSuppliersOptions] = useState([])
  const { startDate, endDate, types, locations, suppliers } = filter
  const dateFormat = 'DD/MM/YYYY'
  const isDateFilterChange = useRef(true)
  // const endpoint = useLocation().pathname.split('/').reverse()[0]
  useEffect(() => {
    if (allSuppliers?.length === 0) dispatch(fetchAllSuppliers())

    const filterCopy = { ...(savedFilter || filter) }
    const sevenDaysBefore = moment().subtract(31, 'days').format(dateFormat)
    const today = moment().format(dateFormat)
    filterCopy.startDate = filterCopy.startDate || sevenDaysBefore
    filterCopy.endDate = filterCopy.endDate || today
    dispatch(setOrdersFilter(filterCopy, venueId))
    dispatch(setOrderedProductsPage(1))
    // dispatch(fetchStatementOrders(venueId, sevenDaysBefore, today))
    // dispatch(fetchOrders(venueId, sevenDaysBefore, today, suppliers, types))
  }, [])

  useEffect(() => {
    if (venueId && startDate && endDate) {
      dispatch(fetchOrders(venueId, startDate, endDate, suppliers, types))
    }
  }, [suppliers, types, startDate, endDate, venueId])

  useEffect(() => {
    if (simplifiedOrders?.length > 0) {
      if (isDateFilterChange.current) {
        setTypesOptions(
          findUnique(
            simplifiedOrders,
            order => order?.type || order?.product?.type || '',
            'keys',
          ).sort((a, b) => (a > b ? 1 : -1)),
        )

        const uniqueSuppliers = findUnique(
          simplifiedOrders,
          order => order?.supplier?._id || '',
          'keys',
        ).filter(Boolean)

        const supplierOptions = uniqueSuppliers
          .map(supplierId => {
            const supplier =
              simplifiedOrders.find(o => o?.supplier?._id === supplierId)
                ?.supplier || {}
            return {
              supplier: supplier._id || '',
              supplierTitle: supplier.title || '',
            }
          })
          .sort((a, b) => a.supplierTitle.localeCompare(b.supplierTitle))

        setSuppliersOptions(supplierOptions)
        isDateFilterChange.current = false
      }
    } else {
      setTypesOptions([])
      setSuppliersOptions([])
    }
  }, [simplifiedOrders])

  const handleRefreshButton = event => {
    dispatch(resetOrdersFilter(venueId))
    event.target.blur()
  }

  const handleStartOpenChange = open => {
    if (!open) setIsEndOpen(true)
  }

  const disabledStartDate = startValue =>
    endDate
      ? startValue.valueOf() > moment(endDate, dateFormat).valueOf()
      : startValue.valueOf() > moment().valueOf()

  const disabledEndDate = endValue =>
    endValue.valueOf() < moment(startDate, dateFormat).valueOf() ||
    endValue.valueOf() > moment().valueOf()

  const onChange = (field, value) => {
    const filterCopy = { ...filter }
    filterCopy[field] =
      field === 'startDate' || field === 'endDate'
        ? value.format(dateFormat)
        : [...value]
    if (field === 'startDate' || field === 'endDate') {
      isDateFilterChange.current = true
    }

    dispatch(setOrdersFilter(filterCopy, venueId))
  }

  const onChangeEndDate = data => {
    isDateFilterChange.current = true
    onChange('endDate', data)
    // dispatch(fetchStatementOrders(venueId, startDate, data.format(dateFormat)), suppliers, types)
    // dispatch(fetchOrders(venueId, startDate, data.format(dateFormat)), suppliers, types)
  }

  return (
    <Wrapper>
      <Form>
        <H3>Filter</H3>
        <DatePicker
          placeholder={startDate}
          disabledDate={disabledStartDate}
          // value={startDate ? moment(startDate, dateFormat) : null}
          format={dateFormat}
          onChange={data => onChange('startDate', data)}
          onOpenChange={handleStartOpenChange}
          // showToday={false}
        />
        <DatePicker
          placeholder={endDate}
          disabledDate={disabledEndDate}
          // value={endDate ? moment(endDate, dateFormat) : null}
          format={dateFormat}
          onChange={onChangeEndDate}
          open={isEndOpen}
          onOpenChange={open => setIsEndOpen(open)}
        />

        {venueId ? (
          <Select
            allowClear
            mode='multiple'
            placeholder='Product type'
            onChange={data => onChange('types', data)}
            value={types}
          >
            {typesOptions.map((type, index) => {
              return (
                <Option key={`${type}${index}`} value={type}>
                  {type}
                </Option>
              )
            })}
          </Select>
        ) : (
          <Select
            allowClear
            mode='multiple'
            placeholder='Location'
            onChange={data => onChange('locations', data)}
            value={locations}
          >
            {venuesOptions.map(venue => (
              <Option key={venue._id}>{venue.title}</Option>
            ))}
          </Select>
        )}
        <Select
          allowClear
          mode='multiple'
          placeholder='Supplier'
          onChange={data => onChange('suppliers', data)}
          value={suppliers}
        >
          {suppliersOptions.map(supplier => (
            <Option key={supplier.supplier} value={supplier.supplier}>
              {supplier.supplierTitle}
            </Option>
          ))}
        </Select>
        <ClearButton onClick={handleRefreshButton}>Clear</ClearButton>
      </Form>
    </Wrapper>
  )
}

const Wrapper = styled.div`
  background-color: ${({ theme }) => theme.colours.mayoWhite};
  border: 1px solid ${({ theme }) => theme.colours.panSilver};
  width: 100%;
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  padding: ${({ theme }) => `${theme.spacing.xs} ${theme.spacing.sm}`};
  border-radius: 5px;
  margin: 0 0 ${({ theme }) => theme.spacing.sm} 0;
  h3 {
    margin: 0;
    flex: 0 0 auto;
    color: ${({ theme }) => theme.colours.oliveGrey};
    font-size: 0.875rem;
    font-weight: 600;
  }
`

const Form = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  margin: 0;
  gap: ${({ theme }) => theme.spacing.xs};
  .ant-calendar-picker,
  .ant-select {
    width: 148px;
    padding-bottom: 0;
  }
  .anticon.anticon-calendar.ant-calendar-picker-icon {
    top: 50%;
  }
  input::placeholder {
    color: ${({ theme }) => theme.colours.oliveGrey};
    font-weight: 500;
  }
`

const ClearButton = styled(Button)`
  flex: 0 0 auto;
  color: ${({ theme }) => theme.colours.oliveGrey};
  background: ${({ theme }) => theme.colours.mayoWhite};
  border: 1px solid ${({ theme }) => theme.colours.panSilver};
`

export default OrdersFilter
